import axios from "axios";
import Cookies from "js-cookie";
import { reportAxiosError, installGlobalErrorLog, setLogContext } from "@/utils/errorLog.mjs";
import { decrypt } from "@/utils/cryptoHelper";

// Import configuration from environment variables
const {
  VUE_APP_BASE_URL,
  VUE_APP_COMPANY,
  VUE_APP_APP_NAME,
  VUE_APP_SUPPORT_EMAIL,
  VUE_APP_WHATSAPP,
  VUE_APP_EMAIL,
  VUE_APP_SSO_URL,
  VUE_APP_REGISTER_AGENT_URL,
  VUE_APP_GOOGLE_AUTH_CLIENT_ID,
  VUE_APP_GOOGLE_AUTH_REDIRECT_URI,
} = process.env;

// Main application configuration
export const MAINCONFIG = {
  BASE_URL: VUE_APP_BASE_URL,
  COMPANY: VUE_APP_COMPANY,
  APP_NAME: VUE_APP_APP_NAME,
  SUPPORT_EMAIL: VUE_APP_SUPPORT_EMAIL,
  timeout: 30000,
  maxRetries: 3,
  WHATSAPP: VUE_APP_WHATSAPP,
  EMAIL: VUE_APP_EMAIL,
  SSO_URL: VUE_APP_SSO_URL,
  REGISTER_AGENT_URL: VUE_APP_REGISTER_AGENT_URL,
  GOOGLE_AUTH_CLIENT_ID: VUE_APP_GOOGLE_AUTH_CLIENT_ID,
  GOOGLE_AUTH_REDIRECT_URI: VUE_APP_GOOGLE_AUTH_REDIRECT_URI,
};

// Axios instance with global configuration
export const API_URL = axios.create({
  baseURL: VUE_APP_BASE_URL,
  timeout: 30000, // Set request timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Gerbang sesi. Dulu `app.mount()` ditahan sampai fingerprint + POST /user/guest
// selesai, jadi pengunjung baru menatap layar KOSONG ~0,8 dtk di atas biaya muat JS.
// Sekarang UI dipasang duluan dan yang menunggu token hanyalah request yang memang
// butuh token — komponennya tetap tampil dengan status loading masing-masing.
//
// Dilepas dari DUA arah supaya tak pernah menggantung:
//   - store mutation `user/setToken` (token guest baru terbit), dan
//   - `finally` bootstrap di main.js (termasuk saat /user/guest gagal — request lalu
//     jalan tanpa token, persis perilaku lama sesudah gagal).
//
// ⚠ Yang digerbangi HAMPIR SEMUA request, bukan cuma yang ber-`requiresAuth`. Token
// dipasang sebagai DEFAULT header di `user/setToken` (store/user.js), jadi endpoint tanpa
// `requiresAuth` pun ikut membawanya begitu sesi terbit — `/city/getAll` dan
// `/property-type/getAllType` termasuk, dan keduanya dijawab 401 tanpa token. Menggerbangi
// `requiresAuth` saja membuat panggilan awal halaman berangkat telanjang lalu 401 (terjadi,
// terukur). Satu-satunya pengecualian = pembuat sesi itu sendiri, kalau tidak ia menunggu
// dirinya sendiri. Sesudah boot promise-nya sudah beres → biayanya satu microtask.
const SESSION_BOOTSTRAP_URL = /\/user\/guest\/?$/;

let markSessionReady;
export const sessionReady = new Promise((resolve) => {
  markSessionReady = resolve;
});
export const releaseSessionGate = () => markSessionReady();

// Axios interceptors (if any)
API_URL.interceptors.request.use(
  async (config) => {
    if (!SESSION_BOOTSTRAP_URL.test(config.url || "")) await sessionReady;
    // Ambil token langsung dari localStorage untuk menghindari circular dependency
    const token = localStorage.getItem("token");

    // Hanya tambahkan token jika `requiresAuth` ada dan bernilai true
    if (config.requiresAuth && token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // CH-W39: lampirkan token atribusi marketing saat membuat cart (jembatan visit -> booking).
    // Hanya endpoint pembuat cart agar tak memicu forbidNonWhitelisted di DTO lain.
    if (
      config.method === "post" &&
      typeof config.url === "string" &&
      config.url.includes("/booking/cart") &&
      config.data &&
      typeof config.data === "object" &&
      !Array.isArray(config.data)
    ) {
      const visitId = localStorage.getItem("mkt_visit_id");
      if (visitId && !config.data.AttributionVisitId) {
        config.data = { ...config.data, AttributionVisitId: visitId };
      }
    }

    // Link tamu: selama berada di halaman /link/exclusive/:slug (atau /exclusive/:slug), setiap
    // panggilan membawa slug-nya. Slug = kredensial yang membuat halaman ini bisa dibuka tanpa
    // login, sekaligus penanda yang dipakai backend mencatat jejak akses & aksi.
    //
    // Dipasang di interceptor, bukan di tiap store action: halaman itu memanggil ~10 endpoint
    // (booking, payments, invoice, generate PIN, cancel extend, extend/reschedule/change room) dan
    // menambahkannya satu per satu berarti selalu ada yang kelewat — termasuk yang ditambah nanti.
    const exclusiveSlug = readExclusiveSlugFromPath(window.location.pathname);
    if (exclusiveSlug) {
      config.params = { ...(config.params || {}), slug: exclusiveSlug };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

function readExclusiveSlugFromPath(pathname) {
  // Anchor sampai akhir path: `/exclusive/cancellation/<code>` punya segmen kedua dan BUKAN
  // halaman link, jadi jangan sampai "cancellation" terbaca sebagai slug.
  const match = String(pathname || "").match(
    /^\/(?:link\/)?exclusive\/([^/?#]+)\/?$/
  );
  return match ? decodeURIComponent(match[1]) : null;
}

// CH-W41: request ber-auth yang ditolak 401 (mis. sesi di-revoke dari device lain,
// atau setelah ganti/reset password) -> bersihkan sesi & arahkan ke login.
// Guard: hanya untuk request yang membawa Authorization (bukan flow guest publik).
// Kunci `?key=` lama ditolak karena booking sudah punya link tamu yang bisa dicabut. Backend
// membalas 401 + kode 117 + slug penggantinya.
//
// HARUS diperiksa SEBELUM cabang sesi-kedaluwarsa di bawah: bentuknya sama-sama 401, jadi kalau
// tidak, customer yang sedang login akan dianggap sesinya mati -> token dihapus -> dilempar ke SSO,
// padahal maksudnya cuma "pindah halaman". Ditangani di interceptor, bukan di halaman, karena
// halaman link menembak beberapa endpoint sekaligus dan yang mana pun bisa kena duluan.
const BOOKING_LINK_MOVED = 117;

API_URL.interceptors.response.use(
  (response) => response,
  (error) => {
    // Kode error + laporan diam-diam ke External Log. Dipasang DI SINI, bukan per store action:
    // yang gagal bisa endpoint mana saja, dan yang ditambah nanti pasti kelewat kalau dipasang
    // satu-satu. Kodenya diingat sebentar lalu ikut tampil di toast (utils/alert.js).
    reportAxiosError(error, VUE_APP_BASE_URL);

    const status = error.response?.status;
    const body = error.response?.data;
    if (status === 401 && body?.code === BOOKING_LINK_MOVED && body?.data?.Slug) {
      const target = `/link/exclusive/${body.data.Slug}`;
      if (window.location.pathname !== target) {
        window.location.replace(target);
      }
      return Promise.reject(error);
    }

    const hadAuth = !!error.config?.headers?.Authorization;
    if (status === 401 && hadAuth && localStorage.getItem("token")) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Error JS / promise yang tak tertangkap ikut tercatat dengan kode yang sama.
// UserId dibaca langsung dari localStorage (pola yang sama dipakai interceptor untuk token) —
// mengimpor store dari sini melahirkan circular dependency. Tamu tanpa akun tetap null; yang
// mengenalinya kolom RequestId (fingerprint perangkat).
setLogContext(() => {
  const raw = localStorage.getItem("user");
  return { UserId: raw ? JSON.parse(decrypt(raw))?.Id ?? null : null };
});
installGlobalErrorLog(VUE_APP_BASE_URL);

export const SETTINGS = {
  WEBSITE_LOGO: "WEBSITE_LOGO"
}

// CH-W37 — switch auth ke SSO account.psggroup.id. true = tak ada form login/register, semua ke SSO.
// ponytail: build-time env (flip butuh rebuild). Upgrade path: baca dari backend setting kalau butuh flip runtime.
export const SSO_ONLY =
  `${process.env.VUE_APP_SSO_ONLY ?? ""}`.trim().toLowerCase() === "true";

// Base URL PSG Account (SSO). Dipakai tombol "Kelola Akun" -> {SSO_ISSUER}/account.
export const SSO_ISSUER = `${process.env.VUE_APP_SSO_ISSUER || ""}`.replace(/\/+$/, "");

// Redirect ke endpoint SSO backend (yang lalu 302 ke account.psggroup.id).
// visitorId dibawa agar guest cart tetap tersambung saat jadi user.
export function ssoLogin(redirect) {
  const base = `${VUE_APP_BASE_URL || ""}`.replace(/\/+$/, "");
  const params = new URLSearchParams();
  if (redirect) params.set("redirect", redirect);
  const visitorId = localStorage.getItem("fingerprintId");
  if (visitorId) params.set("visitorId", visitorId);
  // CH-W37 — host asal (agent bisa punya subdomain sendiri) supaya callback pulang ke sini,
  // bukan ke SSO_FE_URL. Backend hanya menurutinya bila lolos allowlist SSO_FE_ORIGINS.
  params.set("origin", window.location.origin);
  // CH-W37 — teruskan bahasa aktif UI customer (cookie "language", en/id) ke SSO via ui_locales (OIDC).
  const lang = Cookies.get("language");
  if (lang) params.set("ui_locales", lang);
  const qs = params.toString();
  window.location.href = `${base}/user/sso/authorize${qs ? `?${qs}` : ""}`;
}

export default API_URL;
